/**
 * Client-side project assessment calculations (Kravbeskrivelse 27012026)
 * These are pure math — no API needed. Formula source: LOCP_calculator.xlsx.
 */

// ─── Electricity prices ───────────────────────────────────────────────────────

/**
 * PPA prices — long-term power purchase agreement (10–15 yr).
 * Used in ALL LCOP calculations. NOK/MWh.
 * Source: Internal estimate, midpoint of project spec ranges, 2024.
 */
export const ZONE_PRICES = {
  NO4: 550,   // Nord (45–65 øre/kWh, mid 55)
  NO3: 600,   // Midt (50–70 øre/kWh, mid 60)
  NO5: 650,   // Vest (55–75 øre/kWh, mid 65)
  NO2: 680,   // Sør (55–80 øre/kWh, mid 68)
  NO1: 650,   // Øst (55–75 øre/kWh, mid 65)
};

/**
 * Historical day-ahead spot averages — reference only, NOT used in LCOP calculations.
 */
export const SPOT_PRICES_NOK_MWH = {
  NO1: 500, NO2: 480, NO3: 420, NO4: 280, NO5: 460,
};

// PPA range labels for display
export const NVE_POWER_PRICES = {
  NO1: { label: 'NO1 Southeast', ppaRange: '55–75 øre/kWh' },
  NO2: { label: 'NO2 Southwest', ppaRange: '55–80 øre/kWh' },
  NO3: { label: 'NO3 Central',   ppaRange: '50–70 øre/kWh' },
  NO4: { label: 'NO4 North',     ppaRange: '45–65 øre/kWh' },
  NO5: { label: 'NO5 West',      ppaRange: '55–75 øre/kWh' },
};

// Municipality → price area. Mirrors backend/app/api/sites.py
// _COUNTY_TO_ZONE and its Sunnhordland exception (a test keeps them equal).
// Until 2026-10-13 this table knew five county numbers and put everything
// else, Telemark (40xx) and Troms (55xx) included, in NO1.
const COUNTY_TO_ZONE = {
  '03': 'NO1', '31': 'NO1', '32': 'NO1', '33': 'NO1', '34': 'NO1', '30': 'NO1',
  '39': 'NO2', '40': 'NO2', '38': 'NO2', '42': 'NO2', '11': 'NO2',
  '46': 'NO5',
  '15': 'NO3', '50': 'NO3',
  '18': 'NO4', '55': 'NO4', '56': 'NO4', '54': 'NO4',
};
const VESTLAND_NO2 = new Set(['4611', '4612', '4613', '4614', '4615', '4616', '4617']);

export function zoneForKommune(kommunenummer) {
  const s = String(kommunenummer ?? '').trim();
  if (!/^\d{3,4}$/.test(s)) return null;
  const knr = s.padStart(4, '0');
  if (VESTLAND_NO2.has(knr)) return 'NO2';
  return COUNTY_TO_ZONE[knr.slice(0, 2)] || null;
}

// The price zone to show for a site, saved (enrichment nested) or straight
// from a search (flat): its municipality's zone, else the zone the backend
// derived from the municipality. Never the substation's stored zone, which
// came from a latitude/longitude rule and put Bamble in NO1 and Karmøy in
// NO4 or NO5 (2026-10-13 review).
export function siteZone(site) {
  if (!site) return null;
  const e = site.enrichment || {};
  const pp = e._parcel_props || site._parcel_props || {};
  const knr = e.kommunenummer || site.kommunenummer || pp.kommunenummer || pp.kommunenr;
  return zoneForKommune(knr) || e.price_zone || site.price_zone || null;
}

export function getPriceZone(kommunenummer) {
  const zone = zoneForKommune(kommunenummer) || 'NO1';
  return {
    zone,
    price_nok_mwh: ZONE_PRICES[zone],         // PPA price — for calculations
    spot_nok_mwh: SPOT_PRICES_NOK_MWH[zone],  // spot — display only
  };
}

// ─── Plant CAPEX table (from Jan E spreadsheet) ───────────────────────────────
const CAPEX_TABLE = [
  [10,   40],
  [20,   60],
  [50,  105],
  [100, 160],
  [200, 260],
  [300, 360],
  [500, 550],
  [750, 750],
  [1000, 950],
];

/** Plant capex in MUSD — linear interpolation from spreadsheet table. */
export function plantCapexMusd(mw, technology = 'PEM') {
  const techFactor = technology === 'ALK' ? 0.85 : 1.0;
  // Below lower bound
  if (mw <= CAPEX_TABLE[0][0]) {
    return (CAPEX_TABLE[0][1] * mw / CAPEX_TABLE[0][0]) * techFactor;
  }
  // Linear interpolation
  for (let i = 0; i < CAPEX_TABLE.length - 1; i++) {
    const [mw0, c0] = CAPEX_TABLE[i];
    const [mw1, c1] = CAPEX_TABLE[i + 1];
    if (mw >= mw0 && mw <= mw1) {
      return (c0 + (c1 - c0) * (mw - mw0) / (mw1 - mw0)) * techFactor;
    }
  }
  // Above upper bound — 0.7-exponent extrapolation
  const [mwRef, cRef] = CAPEX_TABLE[CAPEX_TABLE.length - 1];
  return cRef * Math.pow(mw / mwRef, 0.7) * techFactor;
}

// ─── Area requirement ────────────────────────────────────────────────────────

/**
 * Area requirement: m²/MW = Base × (MW/20)^(-0.20)
 * PEM base = 230 m²/MW at 20 MW reference
 * AWE base = 300 m²/MW at 20 MW reference
 */
export function calcAreaRequirement(powerMw, technology) {
  const base = technology === 'AWE' ? 300 : 230;
  const m2PerMw = base * Math.pow(powerMw / 20, -0.20);
  const totalM2 = m2PerMw * powerMw;
  const totalHa = totalM2 / 10000;
  return {
    m2PerMw: Math.round(m2PerMw),
    totalM2: Math.round(totalM2),
    totalHa: Math.round(totalHa * 10) / 10,
  };
}

/**
 * Whole-plant footprint for an AMMONIA plant: electrolyser + synthesis
 * loop + air separation + refrigeration + storage tank + jetty area.
 * FFI guideline 20–27 ha at 300 MW (≈ 670–900 m²/MW); small plants are
 * less dense. Base 1000 m²/MW at 20 MW, mild scale economy:
 *   20 MW → ~2.0 ha · 150 MW → ~11.7 ha · 300 MW → ~21.7 ha
 * The electrolyser-only figure (calcAreaRequirement) is kept for the
 * breakdown line.
 */
export function calcAmmoniaPlantArea(powerMw) {
  const m2PerMw = 1000 * Math.pow(Math.max(powerMw, 1) / 20, -0.12);
  const totalM2 = m2PerMw * powerMw;
  return {
    m2PerMw: Math.round(m2PerMw),
    totalM2: Math.round(totalM2),
    totalHa: Math.round(totalM2 / 10000 * 10) / 10,
  };
}

/**
 * Ammonia water split. Process water is FRESH (demineralised):
 * ~1.6–2.5 m³ per tonne NH₃; a 20 MW plant makes ~50 t/day → ~4–6 m³/h,
 * i.e. ≈ 0.25 m³/h per MW. Cooling is the big one and is NOT fresh:
 * once-through seawater ≈ 20 m³/h per MW (~400 m³/h at 20 MW), or a few
 * m³/h of tower make-up. Storage: ~2.5 t NH₃/day per MW; a monthly ship
 * parcel sets the tank size, which is above the 200 t Seveso upper tier
 * at any plant size.
 */
export function calcAmmoniaWater(powerMw) {
  const mw = Math.max(powerMw, 0);
  return {
    processM3h:   Math.round(mw * 0.25 * 10) / 10,
    coolingSeaM3h: Math.round(mw * 20),
    towerMakeupM3h: Math.round(mw * 0.4 * 10) / 10,
    nh3TonnesPerDay: Math.round(mw * 2.5),
    monthlyParcelT:  Math.round(mw * 2.5 * 30),
  };
}

// ─── Voltage fit ─────────────────────────────────────────────────────────────

/**
 * Voltage fit: Good / Conditional / Poor for given MW and available voltage.
 * Norwegian standard levels: 22 / 66 / 132 / 300 / 420 kV
 */
export function calcVoltageFit(voltageKv, powerMw) {
  const norm =
    voltageKv >= 380 ? 420 :
    voltageKv >= 250 ? 300 :
    voltageKv >= 100 ? 132 :
    voltageKv >= 45  ?  66 : 22;

  const bands = {
     22: { good: [0.5, 10],   cond: [0.5, 15],   label: '0.5–10 MW' },
     66: { good: [5,   60],   cond: [5,   80],   label: '5–60 MW'   },
    132: { good: [30,  300],  cond: [10,  400],  label: '30–300 MW' },
    300: { good: [250, 600],  cond: [200, 700],  label: '250–600 MW'},
    420: { good: [300, 1500], cond: [200, 1500], label: '300–1500+ MW' },
  };

  const b = bands[norm] || bands[66];
  const fit =
    powerMw >= b.good[0] && powerMw <= b.good[1] ? 'Good' :
    powerMw >= b.cond[0] && powerMw <= b.cond[1] ? 'Conditional' : 'Poor';

  return { fit, normKv: norm, goodLabel: b.label };
}

/**
 * Required grid-connection voltage for a given MW target.
 *
 * Operator-practice convention for industrial loads in Norway. The
 * 22 kV tier from the hardware table is skipped — it's distribution
 * (residential / light commercial) and not represented in the
 * upstream substation dataset. For utility-scale loads even at 5–20
 * MW, operators typically negotiate connection at 66 kV minimum.
 *
 *   ≤  50 MW → 66 kV  (regional / sub-transmission)
 *    50–150 MW → 132 kV (transmission tier)
 *   150–400 MW → 300 kV (HV transmission)
 *      400 MW+ → 420 kV (EHV, transmission backbone)
 *
 * Boundaries inclusive on the upper side. Must stay in sync with
 * backend/app/services/lcoh_service.py:required_voltage_kv.
 */
export function requiredVoltage(powerMw) {
  if (powerMw <= 50)  return 66;
  if (powerMw <= 150) return 132;
  if (powerMw <= 400) return 300;
  return 420;
}

/**
 * Suggested maximum grid-connection distance (km) for a project of a
 * given total power draw (IT load × PUE for a data centre).
 *
 * Larger projects amortise spur-line CAPEX over more MW so further
 * substations stay economical. Buckets:
 *      <  10 MW total draw → 10 km
 *      <  50 MW            → 20 km
 *      < 150 MW            → 30 km
 *      < 500 MW            → 50 km
 *      ≥ 500 MW            → 75 km
 *
 * Caller passes the TOTAL POWER DRAW, not the IT load. For a DC
 * project: totalPowerDrawMw = ITLoadMw × PUE.
 *
 * Same suggest-then-override pattern as requiredVoltage — the UI
 * lets the user pick anything, but the default tracks project size.
 */
export function suggestedGridDistanceKm(totalPowerDrawMw) {
  if (totalPowerDrawMw < 10)  return 10;
  if (totalPowerDrawMw < 50)  return 20;
  if (totalPowerDrawMw < 150) return 30;
  if (totalPowerDrawMw < 500) return 50;
  return 75;
}

/**
 * Suggested grid-redundancy posture for a project of a given MW.
 *
 * Dual-feed redundancy is a hyperscaler SLA requirement that
 * disproportionately constrains site selection — for a 10 MW
 * hydrogen project it's a phantom cost; for a 100 MW DC it's
 * non-negotiable. The default tracks project size:
 *
 *   <  50 MW → 'any'      (no redundancy gate — small projects don't pay the spread)
 *    50–150 MW → 'dual'   (dual feed required, no kV constraint)
 *   ≥ 150 MW → 'dual-132' (dual feed at transmission tier)
 *
 * Same suggest-then-override pattern as requiredVoltage and
 * suggestedGridDistanceKm — the UI shows the default but the user
 * can pick any of {any, dual, dual-66, dual-132}.
 */
export function suggestedGridRedundancy(powerMw) {
  if (powerMw < 50)  return 'any';
  if (powerMw < 150) return 'dual';
  return 'dual-132';
}

// ─── LCOP presets ─────────────────────────────────────────────────────────────

/**
 * LCOP scenario presets.
 * conservative: PPA+15%, plant capex +20%
 * base:         midpoint PPA, base capex table
 * optimistic:   PPA-10%, plant capex -15%
 */
export const LCOH_PRESETS = {
  conservative: {
    label: 'Conservative',
    ppaFactor: 1.15,
    capexFactor: 1.20,
    wacc: 0.10,
    lifetimeYrs: 25,
    utilization: 0.90,
    color: '#f97316',
  },
  base: {
    label: 'Base Case',
    ppaFactor: 1.00,
    capexFactor: 1.00,
    wacc: 0.07,
    lifetimeYrs: 25,
    utilization: 0.95,
    color: '#22d3ee',
  },
  optimistic: {
    label: 'Optimistic',
    ppaFactor: 0.90,
    capexFactor: 0.85,
    wacc: 0.06,
    lifetimeYrs: 25,
    utilization: 0.95,
    color: '#22c55e',
  },
};

// ─── LCOP calculator ──────────────────────────────────────────────────────────
// Constants
const FX = 10.5;  // NOK/USD

/**
 * LCOP calculation matching backend lcoh_service.py exactly.
 *
 * Inputs:
 *   powerMw          — installed electrolyser capacity (MW)
 *   technology       — 'PEM' | 'AWE'
 *   ppaPrice         — PPA electricity price (NOK/MWh); default from zone
 *   gridDistKm       — transmission distance (km)
 *   roadDistKm       — road access distance (km)
 *   waterDistKm      — water supply distance (km)
 *   preset           — 'conservative' | 'base' | 'optimistic'
 *
 * Returns: { lcop_usd_kg, lcop_nok_kg, capex, opex, annualH2Tonnes }
 */
export function calcLCOH({ powerMw, technology = 'PEM', ppaPrice,
                            gridDistKm = 2, roadDistKm = 1, waterDistKm = 2,
                            preset = 'base', zone = 'NO3' }) {
  const p = LCOH_PRESETS[preset] || LCOH_PRESETS.base;
  const elecPrice = (ppaPrice ?? ZONE_PRICES[zone] ?? 600) * p.ppaFactor;
  const kWhPerKg = technology === 'AWE' ? 50 : 52;

  // Annual H2 production
  const h2_t_yr = powerMw * 8760 * p.utilization / kWhPerKg;  // tonnes/yr

  // CAPEX (MUSD)
  const plantCapex = plantCapexMusd(powerMw, technology) * p.capexFactor;
  const transmissionCapex = gridDistKm * 4 / FX;
  const roadCapex = roadDistKm * 20 / FX;
  const waterCapex = waterDistKm * 10 / FX;
  const capexTotal = plantCapex + transmissionCapex + roadCapex + waterCapex;

  // OPEX annual (MUSD/yr)
  const opexElec = h2_t_yr * kWhPerKg * elecPrice / 1e6 / FX;
  const opexOther = 5.0 * Math.pow(powerMw / 100, 0.7);
  const opexTotal = opexElec + opexOther;

  // Annuity factor
  const r = p.wacc, n = p.lifetimeYrs;
  const af = (1 - Math.pow(1 + r, -n)) / r;

  const pvOpex = opexTotal * af;
  const pvOutput = h2_t_yr * af;  // tonnes

  const lcop_usd_kg = (capexTotal + pvOpex) * 1e6 / (pvOutput * 1000);
  const lcop_nok_kg = lcop_usd_kg * FX;

  return {
    lcop_usd_kg: Math.round(lcop_usd_kg * 100) / 100,
    lcop_nok_kg: Math.round(lcop_nok_kg * 10) / 10,
    capex: {
      plant: Math.round(plantCapex * 10) / 10,
      transmission: Math.round(transmissionCapex * 100) / 100,
      road: Math.round(roadCapex * 100) / 100,
      water: Math.round(waterCapex * 100) / 100,
      total: Math.round(capexTotal * 10) / 10,
    },
    opex: {
      electricity: Math.round(opexElec * 10) / 10,
      other: Math.round(opexOther * 10) / 10,
      pvTotal: Math.round(pvOpex * 10) / 10,
    },
    annualH2Tonnes: Math.round(h2_t_yr),
    elecPrice,
  };
}

// ─── Water needs ─────────────────────────────────────────────────────────────

/**
 * Water consumption:
 *   with seawater access  → 200 l/hr/MW
 *   without seawater      → 800 l/hr/MW
 */
export function calcWaterNeeds(powerMw, hasSeawaterAccess) {
  const lhrPerMw = hasSeawaterAccess ? 200 : 800;
  const totalM3hr = Math.round((lhrPerMw * powerMw / 1000) * 10) / 10;
  const totalM3day = Math.round(totalM3hr * 24);
  return { lhrPerMw, totalM3hr, totalM3day };
}
